import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Layout,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Tabs,
  Typography,
  Upload,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  LogoutOutlined,
  PlusOutlined,
  SaveOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import api, { API_BASE_URL } from "../api";

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

const parseLines = (value) =>
  (value || "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
const joinLines = (value) => (Array.isArray(value) ? value.join("\n") : "");

const metadataToFields = (section) => {
  const metadata = section.metadata || {};
  return {
    ...section,
    primary_cta: metadata.primary_cta,
    secondary_cta: metadata.secondary_cta,
    stats: metadata.stats || [],
    feature_card: metadata.feature_card || {},
    service_items: (metadata.items || []).map((item) => ({
      ...item,
      features_text: joinLines(item.features),
    })),
    process: metadata.process || [],
    process_title: metadata.process_title,
    process_subtitle: metadata.process_subtitle,
    cta: metadata.cta || {},
    achievements: metadata.achievements || [],
    values: metadata.values || [],
    values_title: metadata.values_title,
    badge: metadata.badge || {},
    phone: metadata.phone,
    email: metadata.email,
    address: metadata.address,
    hours: metadata.hours,
    whatsapp: metadata.whatsapp,
    consultation_project_types: metadata.consultation_project_types || [],
    consultation_budgets: metadata.consultation_budgets || [],
    consultation_timelines: metadata.consultation_timelines || [],
  };
};

const sectionMetadata = (key, values) => {
  switch (key) {
    case "hero":
      return {
        primary_cta: values.primary_cta || "",
        secondary_cta: values.secondary_cta || "",
        stats: values.stats || [],
        feature_card:
          values.feature_card?.title ||
          values.feature_card?.subtitle ||
          values.feature_card?.image_url
            ? values.feature_card
            : undefined,
      };
    case "services":
      return {
        items: (values.service_items || [])
          .filter((item) => item?.title)
          .map((item) => ({
            title: item.title,
            description: item.description || "",
            features: parseLines(item.features_text),
          })),
        process: (values.process || [])
          .filter((item) => item?.title)
          .map((item) => ({
            step: item.step || "",
            title: item.title,
            duration: item.duration || "",
          })),
        process_title: values.process_title || "",
        process_subtitle: values.process_subtitle || "",
        cta:
          values.cta?.title || values.cta?.subtitle || values.cta?.button_label
            ? values.cta
            : undefined,
      };
    case "about":
      return {
        achievements: (values.achievements || []).filter(
          (item) => item?.label || item?.text,
        ),
        values: (values.values || []).filter((item) => item?.title),
        values_title: values.values_title || "",
        badge:
          values.badge?.title || values.badge?.subtitle
            ? values.badge
            : undefined,
      };
    case "contact":
      return {
        phone: values.phone || "",
        email: values.email || "",
        address: values.address || "",
        hours: values.hours || "",
        whatsapp: values.whatsapp || "",
        consultation_project_types: (values.consultation_project_types || [])
          .map((item) => item?.trim())
          .filter(Boolean),
        consultation_budgets: (values.consultation_budgets || [])
          .map((item) => item?.trim())
          .filter(Boolean),
        consultation_timelines: (values.consultation_timelines || [])
          .map((item) => item?.trim())
          .filter(Boolean),
      };
    default:
      return {};
  }
};

const SectionFields = ({ sectionKey }) => {
  const addButton = (add, label) => (
    <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()}>
      {label}
    </Button>
  );
  if (sectionKey === "hero")
    return (
      <>
        <Divider orientation="left">Buttons</Divider>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label="Primary button label" name="primary_cta">
              <Input placeholder="View Portfolio" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Secondary button label" name="secondary_cta">
              <Input placeholder="Schedule Consultation" />
            </Form.Item>
          </Col>
        </Row>
        <Divider orientation="left">Highlights</Divider>
        <Form.List name="stats">
          {(fields, { add, remove }) => (
            <Space direction="vertical" style={{ width: "100%" }}>
              {fields.map(({ key, name }) => (
                <Row key={key} gutter={12} align="middle">
                  <Col flex="auto">
                    <Form.Item
                      name={[name, "label"]}
                      rules={[{ required: true, message: "Add a label" }]}
                    >
                      <Input placeholder="Label, e.g. Projects completed" />
                    </Form.Item>
                  </Col>
                  <Col flex="180px">
                    <Form.Item name={[name, "value"]}>
                      <Input placeholder="Value, e.g. 150+" />
                    </Form.Item>
                  </Col>
                  <Col>
                    <Button
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      aria-label="Remove highlight"
                      onClick={() => remove(name)}
                    />
                  </Col>
                </Row>
              ))}
              {addButton(add, "Add highlight")}
            </Space>
          )}
        </Form.List>
        <Divider orientation="left">Floating feature card (optional)</Divider>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label="Feature title" name={["feature_card", "title"]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Feature subtitle"
              name={["feature_card", "subtitle"]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="Feature image URL"
          name={["feature_card", "image_url"]}
        >
          <Input placeholder="https://..." />
        </Form.Item>
      </>
    );
  if (sectionKey === "services")
    return (
      <>
        <Divider orientation="left">Services</Divider>
        <Form.List name="service_items">
          {(fields, { add, remove }) => (
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              {fields.map(({ key, name }, index) => (
                <Card
                  key={key}
                  size="small"
                  title={`Service ${index + 1}`}
                  extra={
                    <Button
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => remove(name)}
                    >
                      Remove
                    </Button>
                  }
                >
                  <Form.Item
                    label="Service name"
                    name={[name, "title"]}
                    rules={[{ required: true, message: "Add a service name" }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item label="Description" name={[name, "description"]}>
                    <TextArea rows={2} />
                  </Form.Item>
                  <Form.Item
                    label="Included features"
                    name={[name, "features_text"]}
                    extra="Enter one feature per line."
                  >
                    <TextArea rows={3} />
                  </Form.Item>
                </Card>
              ))}
              {addButton(add, "Add service")}
            </Space>
          )}
        </Form.List>
        <Divider orientation="left">Process (optional)</Divider>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label="Process heading" name="process_title">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Process introduction" name="process_subtitle">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Form.List name="process">
          {(fields, { add, remove }) => (
            <Space direction="vertical" style={{ width: "100%" }}>
              {fields.map(({ key, name }) => (
                <Row key={key} gutter={12}>
                  <Col flex="90px">
                    <Form.Item name={[name, "step"]}>
                      <Input placeholder="01" />
                    </Form.Item>
                  </Col>
                  <Col flex="auto">
                    <Form.Item name={[name, "title"]}>
                      <Input placeholder="Step title" />
                    </Form.Item>
                  </Col>
                  <Col flex="180px">
                    <Form.Item name={[name, "duration"]}>
                      <Input placeholder="Duration" />
                    </Form.Item>
                  </Col>
                  <Col>
                    <Button
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => remove(name)}
                    />
                  </Col>
                </Row>
              ))}
              {addButton(add, "Add process step")}
            </Space>
          )}
        </Form.List>
        <Divider orientation="left">Call to action (optional)</Divider>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label="Heading" name={["cta", "title"]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Button label" name={["cta", "button_label"]}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Message" name={["cta", "subtitle"]}>
          <TextArea rows={2} />
        </Form.Item>
        <Form.Item label="Button link" name={["cta", "href"]}>
          <Input placeholder="#contact" />
        </Form.Item>
      </>
    );
  if (sectionKey === "about")
    return (
      <>
        <Divider orientation="left">Achievements</Divider>
        <Form.List name="achievements">
          {(fields, { add, remove }) => (
            <Space direction="vertical" style={{ width: "100%" }}>
              {fields.map(({ key, name }) => (
                <Row key={key} gutter={12}>
                  <Col flex="160px">
                    <Form.Item name={[name, "number"]}>
                      <Input placeholder="Number, e.g. 15+" />
                    </Form.Item>
                  </Col>
                  <Col flex="auto">
                    <Form.Item name={[name, "text"]}>
                      <Input placeholder="Description" />
                    </Form.Item>
                  </Col>
                  <Col>
                    <Button
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => remove(name)}
                    />
                  </Col>
                </Row>
              ))}
              {addButton(add, "Add achievement")}
            </Space>
          )}
        </Form.List>
        <Divider orientation="left">Values</Divider>
        <Form.Item label="Values heading" name="values_title">
          <Input />
        </Form.Item>
        <Form.List name="values">
          {(fields, { add, remove }) => (
            <Space direction="vertical" style={{ width: "100%" }}>
              {fields.map(({ key, name }, index) => (
                <Card
                  key={key}
                  size="small"
                  title={`Value ${index + 1}`}
                  extra={
                    <Button
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => remove(name)}
                    >
                      Remove
                    </Button>
                  }
                >
                  <Form.Item label="Title" name={[name, "title"]}>
                    <Input />
                  </Form.Item>
                  <Form.Item label="Description" name={[name, "description"]}>
                    <TextArea rows={2} />
                  </Form.Item>
                </Card>
              ))}
              {addButton(add, "Add value")}
            </Space>
          )}
        </Form.List>
        <Divider orientation="left">Image badge (optional)</Divider>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label="Badge title" name={["badge", "title"]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Badge subtitle" name={["badge", "subtitle"]}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </>
    );
  if (sectionKey === "contact")
    return (
      <>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label="Phone" name="phone">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Email"
              name="email"
              rules={[{ type: "email", message: "Enter a valid email" }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Address" name="address">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Business hours" name="hours">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="WhatsApp number"
              name="whatsapp"
              extra="Include country code, without + or spaces."
            >
              <Input placeholder="012-3456789" />
            </Form.Item>
          </Col>
        </Row>
        <Divider orientation="left">Free consultation form options</Divider>
        {[
          ["consultation_project_types", "Project types", "Add project type", "e.g. Full Home Renovation"],
          ["consultation_budgets", "Budget ranges", "Add budget range", "e.g. RM50,000 - RM100,000"],
          ["consultation_timelines", "Timelines", "Add timeline", "e.g. 1-3 Months"],
        ].map(([name, label, addLabel, placeholder]) => (
          <Form.List key={name} name={name}>
            {(fields, { add, remove }) => (
              <Form.Item label={label} extra="These choices appear in the Free Consultation Request form.">
                <Space direction="vertical" style={{ width: "100%" }}>
                  {fields.map(({ key, name: itemName }) => (
                    <Space key={key} style={{ display: "flex" }}>
                      <Form.Item name={itemName} noStyle>
                        <Input placeholder={placeholder} style={{ width: 320 }} />
                      </Form.Item>
                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        aria-label={`Remove ${label}`}
                        onClick={() => remove(itemName)}
                      />
                    </Space>
                  ))}
                  {addButton(add, addLabel)}
                </Space>
              </Form.Item>
            )}
          </Form.List>
        ))}
      </>
    );
  return null;
};

const AdminDashboard = () => {
  const [token, setToken] = useState(localStorage.getItem("adminToken"));
  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [projects, setProjects] = useState([]);
  const [googleDriveStatus, setGoogleDriveStatus] = useState({
    configured: false,
    connected: false,
    account_email: "",
  });
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [sectionUploads, setSectionUploads] = useState({});
  const [projectUploads, setProjectUploads] = useState({
    main: null,
    before: null,
  });
  const [galleryUploads, setGalleryUploads] = useState([]);
  const [loginForm] = Form.useForm();
  const [projectForm] = Form.useForm();

  useEffect(() => {
    if (token) loadAdminData();
  }, [token]);
  const loadAdminData = async () => {
    setLoading(true);
    try {
      const [sectionsResponse, projectsResponse, driveStatusResponse] =
        await Promise.all([
          api.get("/sections/"),
          api.get("/projects/"),
          api.get("/admin/google-drive/status/"),
        ]);
      setSections(sectionsResponse.data);
      setProjects(projectsResponse.data);
      setGoogleDriveStatus(driveStatusResponse.data);
    } catch {
      message.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };
  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const response = await api.post("/admin/login/", values);
      localStorage.setItem("adminToken", response.data.access);
      localStorage.setItem("adminRefreshToken", response.data.refresh);
      setToken(response.data.access);
      message.success("Logged in");
    } catch {
      message.error("Invalid admin username or password");
    } finally {
      setLoading(false);
    }
  };
  const connectGoogleDrive = async () => {
    try {
      const response = await api.post("/admin/google-drive/connect/");
      window.location.href = response.data.auth_url;
    } catch (error) {
      message.error(
        error.response?.data?.detail || "Google Drive connect failed",
      );
    }
  };
  const handleLogout = async () => {
    try {
      await api.post("/admin/logout/");
    } finally {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminRefreshToken");
      setToken(null);
    }
  };
  const stageImage = (file, update) => {
    const previewUrl = URL.createObjectURL(file);
    update({ file, previewUrl });
    message.info("Image ready to preview. It will upload when you save.");
  };
  const imageUploadProps = (onSelect) => ({
    accept: "image/*",
    showUploadList: false,
    customRequest: ({ file, onSuccess }) => {
      onSelect(file);
      onSuccess("staged");
    },
  });
  const uploadImage = async (file, oldFileId = "") => {
    const formData = new FormData();
    formData.append("image", file);
    if (oldFileId) formData.append("file_id", oldFileId);
    const response = await api.post("/admin/upload-image/", formData);
    return {
      image_url: `${API_BASE_URL}/images/${response.data.file_id}/`,
      image_file_id: response.data.file_id,
    };
  };
  const saveSection = async (section, values) => {
    try {
      const stagedImage = sectionUploads[section.key];
      const uploadedImage = stagedImage
        ? await uploadImage(stagedImage.file, section.image_file_id)
        : null;
      const payload = {
        ...section,
        title: values.title,
        subtitle: values.subtitle || "",
        body: values.body || "",
        image_url: uploadedImage?.image_url || values.image_url || "",
        image_file_id:
          uploadedImage?.image_file_id || section.image_file_id || "",
        metadata: sectionMetadata(section.key, values),
      };
      const response = await api.put(`/sections/${section.key}/`, payload);
      setSections((current) =>
        current.map((item) =>
          item.key === section.key ? response.data : item,
        ),
      );
      if (stagedImage) {
        URL.revokeObjectURL(stagedImage.previewUrl);
        setSectionUploads((current) => {
          const next = { ...current };
          delete next[section.key];
          return next;
        });
      }
      message.success(`${section.key} section saved`);
    } catch (error) {
      message.error(
        error.response?.data?.detail ||
          "Section save failed. Please check the highlighted fields.",
      );
    }
  };
  const openProjectModal = (project) => {
    setEditingProjectId(project?.id || null);
    setProjectUploads({ main: null, before: null });
    setGalleryUploads([]);
    projectForm.resetFields();
    projectForm.setFieldsValue(
      project
        ? {
            ...project,
            image_file_id: project.image_file_id || "",
            before_image_file_id: project.before_image_file_id || "",
            challenges_text: joinLines(project.challenges),
            solutions_text: joinLines(project.solutions),
            materials_text: joinLines(project.materials),
            gallery_items: (project.gallery || []).map((url) => ({ url })),
          }
        : {
            is_published: true,
            sort_order: 0,
            image_file_id: "",
            before_image_file_id: "",
            gallery_items: [],
          },
    );
    setIsProjectModalOpen(true);
  };
  const closeProjectModal = () => {
    Object.values(projectUploads).forEach(
      (upload) => upload && URL.revokeObjectURL(upload.previewUrl),
    );
    galleryUploads.forEach((upload) => URL.revokeObjectURL(upload.previewUrl));
    setProjectUploads({ main: null, before: null });
    setGalleryUploads([]);
    setIsProjectModalOpen(false);
  };
  const saveProject = async (values) => {
    const payload = {
      ...values,
      challenges: parseLines(values.challenges_text),
      solutions: parseLines(values.solutions_text),
      materials: parseLines(values.materials_text),
      gallery: (values.gallery_items || [])
        .map((item) => item?.url?.trim())
        .filter(Boolean),
    };
    delete payload.challenges_text;
    delete payload.solutions_text;
    delete payload.materials_text;
    delete payload.gallery_items;
    try {
      if (projectUploads.main)
        Object.assign(
          payload,
          await uploadImage(projectUploads.main.file, values.image_file_id),
        );
      if (projectUploads.before) {
        const uploadedBefore = await uploadImage(
          projectUploads.before.file,
          values.before_image_file_id,
        );
        payload.before_image_url = uploadedBefore.image_url;
        payload.before_image_file_id = uploadedBefore.image_file_id;
      }
      if (galleryUploads.length) {
        const uploadedGallery = await Promise.all(
          galleryUploads.map((upload) => uploadImage(upload.file)),
        );
        payload.gallery = [
          ...payload.gallery,
          ...uploadedGallery.map((upload) => upload.image_url),
        ];
      }
      const response = editingProjectId
        ? await api.put(`/projects/${editingProjectId}/`, payload)
        : await api.post("/projects/", payload);
      setProjects((current) =>
        editingProjectId
          ? current.map((project) =>
              project.id === editingProjectId ? response.data : project,
            )
          : [response.data, ...current],
      );
      projectForm.resetFields();
      setEditingProjectId(null);
      closeProjectModal();
      message.success("Project saved");
    } catch (error) {
      message.error(error.response?.data?.detail || "Project save failed");
    }
  };
  const deleteProject = async (projectId) => {
    try {
      await api.delete(`/projects/${projectId}/`);
      setProjects((current) =>
        current.filter((project) => project.id !== projectId),
      );
      message.success("Project deleted");
    } catch {
      message.error("Delete failed");
    }
  };

  if (!token)
    return (
      <Content style={{ maxWidth: 460, margin: "100px auto", width: "100%" }}>
        <Card>
          <Title level={2}>Admin Login</Title>
          <Text type="secondary">
            Manage website content without editing code.
          </Text>
          <Form
            layout="vertical"
            form={loginForm}
            onFinish={handleLogin}
            style={{ marginTop: 24 }}
          >
            <Form.Item
              label="Username"
              name="username"
              rules={[{ required: true }]}
            >
              <Input autoComplete="username" />
            </Form.Item>
            <Form.Item
              label="Password"
              name="password"
              rules={[{ required: true }]}
            >
              <Input.Password autoComplete="current-password" />
            </Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              Login
            </Button>
          </Form>
        </Card>
      </Content>
    );
  if (loading && !sections.length)
    return (
      <Content style={{ padding: 100, textAlign: "center" }}>
        <Spin size="large" />
      </Content>
    );

  return (
    <Content
      style={{
        padding: "40px 24px",
        maxWidth: 1200,
        margin: "70px auto 0",
        width: "100%",
      }}
    >
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Website Manager
          </Title>
          <Text type="secondary">
            Update your website using simple content fields.
          </Text>
        </div>
        <Button danger icon={<LogoutOutlined />} onClick={handleLogout}>
          Logout
        </Button>
      </Space>
      <Alert
        message={
          googleDriveStatus.connected
            ? `Google Drive connected${googleDriveStatus.account_email ? ` as ${googleDriveStatus.account_email}` : ""}.`
            : googleDriveStatus.configured
              ? "Connect Google Drive to upload images."
              : "Image uploads need Google Drive to be configured."
        }
        type={googleDriveStatus.connected ? "success" : "warning"}
        action={
          <Button
            icon={<LinkOutlined />}
            size="small"
            onClick={connectGoogleDrive}
          >
            Connect Google Drive
          </Button>
        }
        showIcon
        style={{ marginBottom: 24 }}
      />
      <Tabs
        items={[
          {
            key: "sections",
            label: "Website content",
            children: (
              <Space
                direction="vertical"
                size="large"
                style={{ width: "100%" }}
              >
                {sections.map((section) => (
                  <Card
                    key={section.key}
                    title={
                      section.key === "hero"
                        ? "Homepage hero"
                        : section.key === "about"
                          ? "About us"
                          : section.key === "contact"
                            ? "Contact & consultation"
                            : "Services"
                    }
                    extra={
                      <Text type="secondary">
                        Changes are saved section by section
                      </Text>
                    }
                  >
                    <Form
                      layout="vertical"
                      initialValues={metadataToFields(section)}
                      onFinish={(values) => saveSection(section, values)}
                    >
                      <Row gutter={16}>
                        <Col xs={24} md={12}>
                          <Form.Item
                            label="Heading"
                            name="title"
                            rules={[
                              {
                                required: true,
                                message: "A heading is required",
                              },
                            ]}
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item label="Subheading" name="subtitle">
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item label="Main text" name="body">
                        <TextArea rows={3} />
                      </Form.Item>
                      <Form.Item
                        label="Section image"
                        extra="Choose an image to preview it. It will only upload after you save this section."
                      >
                        {(sectionUploads[section.key]?.previewUrl ||
                          section.image_url) && (
                          <>
                            <Text
                              strong
                              style={{ display: "block", marginBottom: 8 }}
                            >
                              {sectionUploads[section.key]
                                ? "Selected replacement preview"
                                : "Current image"}
                            </Text>
                            <img
                              src={
                                sectionUploads[section.key]?.previewUrl ||
                                section.image_url
                              }
                              alt="Section preview"
                              style={{
                                display: "block",
                                maxWidth: "100%",
                                width: 420,
                                height: "auto",
                                maxHeight: 360,
                                objectFit: "contain",
                                borderRadius: 8,
                                marginBottom: 12,
                                border: "1px solid #d9d9d9",
                              }}
                            />
                          </>
                        )}
                        <Upload
                          {...imageUploadProps((file) =>
                            stageImage(file, (upload) =>
                              setSectionUploads((current) => ({
                                ...current,
                                [section.key]: upload,
                              })),
                            ),
                          )}
                        >
                          <Button icon={<UploadOutlined />}>
                            Choose image
                          </Button>
                        </Upload>
                        <Form.Item name="image_url" hidden>
                          <Input />
                        </Form.Item>
                      </Form.Item>
                      <SectionFields sectionKey={section.key} />
                      <Divider />
                      <Button
                        type="primary"
                        htmlType="submit"
                        icon={<SaveOutlined />}
                      >
                        Save {section.key} section
                      </Button>
                    </Form>
                  </Card>
                ))}
              </Space>
            ),
          },
          {
            key: "projects",
            label: "Portfolio projects",
            children: (
              <Card>
                <Space
                  style={{
                    width: "100%",
                    justifyContent: "space-between",
                    marginBottom: 16,
                  }}
                >
                  <Text>
                    Create and maintain projects shown in your portfolio.
                  </Text>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => openProjectModal(null)}
                  >
                    Add project
                  </Button>
                </Space>
                <Table
                  rowKey="id"
                  dataSource={projects}
                  pagination={{ pageSize: 8 }}
                  columns={[
                    { title: "Title", dataIndex: "title" },
                    { title: "Category", dataIndex: "category" },
                    { title: "Location", dataIndex: "location" },
                    {
                      title: "Status",
                      render: (_, record) =>
                        record.is_published ? "Published" : "Draft",
                    },
                    {
                      title: "Actions",
                      render: (_, record) => (
                        <Space>
                          <Button
                            icon={<EditOutlined />}
                            onClick={() => openProjectModal(record)}
                          >
                            Edit
                          </Button>
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() =>
                              Modal.confirm({
                                title: "Delete this project?",
                                content: "This cannot be undone.",
                                okText: "Delete",
                                okButtonProps: { danger: true },
                                onOk: () => deleteProject(record.id),
                              })
                            }
                          >
                            Delete
                          </Button>
                        </Space>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
        ]}
      />
      <Modal
        className="project-editor-modal"
        title={editingProjectId ? "Edit project" : "Add project"}
        open={isProjectModalOpen}
        maskClosable={false}
        onCancel={closeProjectModal}
        footer={null}
        width={820}
      >
        <Form
          className="project-editor"
          layout="vertical"
          form={projectForm}
          onFinish={saveProject}
        >
          <Form.Item name="image_file_id" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="before_image_file_id" hidden>
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Project title"
                name="title"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Category"
                name="category"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Location" name="location">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Completion date" name="date">
                <Input placeholder="2026 or June 2026" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Description" name="description">
            <TextArea rows={3} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Main image URL" name="image_url">
                <Input />
              </Form.Item>
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) => {
                  const previewUrl =
                    projectUploads.main?.previewUrl ||
                    getFieldValue("image_url");
                  return (
                    <>
                      {previewUrl && (
                        <img
                          src={previewUrl}
                          alt="Main project preview"
                          style={{
                            display: "block",
                            width: "100%",
                            maxHeight: 250,
                            objectFit: "contain",
                            borderRadius: 8,
                            border: "1px solid #d9d9d9",
                            marginBottom: 12,
                          }}
                        />
                      )}
                      <Upload
                        {...imageUploadProps((file) =>
                          stageImage(file, (upload) =>
                            setProjectUploads((current) => ({
                              ...current,
                              main: upload,
                            })),
                          ),
                        )}
                      >
                        <Button icon={<UploadOutlined />}>
                          Choose main image
                        </Button>
                      </Upload>
                    </>
                  );
                }}
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Before image URL" name="before_image_url">
                <Input />
              </Form.Item>
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) => {
                  const previewUrl =
                    projectUploads.before?.previewUrl ||
                    getFieldValue("before_image_url");
                  return (
                    <>
                      {previewUrl && (
                        <img
                          src={previewUrl}
                          alt="Before project preview"
                          style={{
                            display: "block",
                            width: "100%",
                            maxHeight: 250,
                            objectFit: "contain",
                            borderRadius: 8,
                            border: "1px solid #d9d9d9",
                            marginBottom: 12,
                          }}
                        />
                      )}
                      <Upload
                        {...imageUploadProps((file) =>
                          stageImage(file, (upload) =>
                            setProjectUploads((current) => ({
                              ...current,
                              before: upload,
                            })),
                          ),
                        )}
                      >
                        <Button icon={<UploadOutlined />}>
                          Choose before image
                        </Button>
                      </Upload>
                    </>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={24} md={12}>
              <Form.Item label="Client" name="client">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Duration" name="duration">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Size" name="size">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Budget" name="budget">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Challenges"
                name="challenges_text"
                extra="One item per line."
              >
                <TextArea rows={4} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Solutions"
                name="solutions_text"
                extra="One item per line."
              >
                <TextArea rows={4} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Materials"
                name="materials_text"
                extra="One item per line."
              >
                <TextArea rows={4} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Divider orientation="left">Project gallery</Divider>
              <Text type="secondary">
                Add image links individually, or choose files to preview and
                upload when you save.
              </Text>
              <Form.List name="gallery_items">
                {(fields, { add, remove }) => (
                  <Space
                    direction="vertical"
                    style={{ width: "100%", marginTop: 16 }}
                  >
                    {fields.map(({ key, name }, index) => (
                      <Row key={key} gutter={12} align="middle">
                        <Col flex="42px">
                          <Text strong>{index + 1}</Text>
                        </Col>
                        <Col flex="auto">
                          <Form.Item
                            name={[name, "url"]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input placeholder="https://..." />
                          </Form.Item>
                        </Col>
                        <Col>
                          <Button
                            danger
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                          >
                            Remove
                          </Button>
                        </Col>
                      </Row>
                    ))}
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => add({ url: "" })}
                    >
                      Add image URL
                    </Button>
                  </Space>
                )}
              </Form.List>
              <Upload
                multiple
                {...imageUploadProps((file) =>
                  stageImage(file, (upload) =>
                    setGalleryUploads((current) => [...current, upload]),
                  ),
                )}
              >
                <Button icon={<UploadOutlined />} style={{ marginTop: 16 }}>
                  Choose gallery images
                </Button>
              </Upload>
              {galleryUploads.length > 0 && (
                <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
                  {galleryUploads.map((upload, index) => (
                    <Col xs={12} sm={8} md={6} key={upload.previewUrl}>
                      <Card
                        size="small"
                        cover={
                          <img
                            src={upload.previewUrl}
                            alt={`New gallery preview ${index + 1}`}
                            style={{ height: 110, objectFit: "contain" }}
                          />
                        }
                        actions={[
                          <Button
                            key="remove"
                            danger
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              URL.revokeObjectURL(upload.previewUrl);
                              setGalleryUploads((current) =>
                                current.filter((item) => item !== upload),
                              );
                            }}
                          >
                            Remove
                          </Button>,
                        ]}
                      >
                        <Text ellipsis>New image {index + 1}</Text>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Col>
          </Row>
          <Space>
            <Form.Item
              name="is_published"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Checkbox>Publish this project</Checkbox>
            </Form.Item>
            <Form.Item
              label="Display order"
              name="sort_order"
              style={{ marginBottom: 0 }}
            >
              <InputNumber min={0} />
            </Form.Item>
          </Space>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            block
            style={{ marginTop: 24 }}
          >
            Save project
          </Button>
        </Form>
      </Modal>
    </Content>
  );
};

export default AdminDashboard;
